import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import { TaskTypeStatus, type TaskCardType, type TaskHistoryItemType } from '@econtractor/types';
import { UserNameItem } from 'modules/user';
import { parseHistoryComment } from '../../shared/helpers/taskDueDateHelpers';
import SolarIcon from 'components/icons/SolarIcon';

interface TaskCardHistoryTabProps {
    task: TaskCardType;
}

const formatEventAt = (millis: number): string => {
    if (!millis) return '—';
    const dt = DT.fromMillis(millis, { zone: 'local' });
    return dt.isValid ? dt.toLocaleString(DT.DATETIME_SHORT) : '—';
};

const statusDotClass = (status: number): string => {
    switch (status) {
        case TaskTypeStatus.NEW:
            return 'secondary';
        case TaskTypeStatus.INWORK:
            return 'warning';
        case TaskTypeStatus.COMPLETED:
            return 'primary';
        case TaskTypeStatus.ACCEPTED:
            return 'success';
        case TaskTypeStatus.CANCELLED:
            return 'danger';
        case TaskTypeStatus.REJECT:
            return 'secondary';
        default:
            return 'secondary';
    }
};

const HistoryItem = ({ item }: { item: TaskHistoryItemType }) => {
    const { t } = useTranslation(['Tasks']);
    const parsed = parseHistoryComment(item.rejectComment);
    const hasComment = Boolean(parsed.text.trim());
    const status = Number(item.taskStatus);
    const dot = statusDotClass(status);

    const isSpecialHistory =
        parsed.kind === 'term' || parsed.kind === 'users' || parsed.kind === 'remind';
    const specialLabel =
        parsed.kind === 'term'
            ? t('history.termChanged')
            : parsed.kind === 'users'
              ? t('history.usersChanged')
              : parsed.kind === 'remind'
                ? t('history.reminded')
                : '';

    const commentLabel = isSpecialHistory
        ? specialLabel
        : status === TaskTypeStatus.COMPLETED
          ? t('labelAnswer')
          : t('labelRejectComment');

    const itemDot = parsed.kind === 'remind' ? 'info' : dot;

    return (
        <li className="task-history-timeline__item">
            <i className={`task-history-timeline__dot bg-${itemDot}`} aria-hidden="true"></i>
            <div className="task-history-timeline__body">
                <div className="task-history-timeline__row">
                    <span className="task-history-timeline__label">
                        {isSpecialHistory
                            ? specialLabel
                            : t(`status.${status}`, { defaultValue: String(status) })}
                    </span>
                    <span className="task-history-timeline__date">
                        {formatEventAt(Number(item.dt || 0))}
                    </span>
                </div>

                <div className="task-history-timeline__author">
                    {item.authorUser?.ID ? (
                        <UserNameItem user={item.authorUser} />
                    ) : (
                        <span className="text-muted">—</span>
                    )}
                </div>

                {hasComment && (
                    <div className="task-history-timeline__reject">
                        {!isSpecialHistory && (
                            <div className="task-history-timeline__reject-label">{commentLabel}</div>
                        )}
                        <div className="task-history-timeline__reject-text">{parsed.text}</div>
                    </div>
                )}
            </div>
        </li>
    );
};

const TaskCardHistoryTab = ({ task }: TaskCardHistoryTabProps) => {
    const { t } = useTranslation(['Tasks']);
    const history = Array.isArray(task.history) ? [...task.history].reverse() : [];

    if (!history.length) {
        return (
            <div className="task-card-empty">
                <SolarIcon name="clock-history" className="me-2" />
                {t('emptyHistory')}
            </div>
        );
    }

    return (
        <ul className="task-history-timeline list-unstyled mb-0">
            {history.map((item) => (
                <HistoryItem key={item.ID || `${item.dt}-${item.taskStatus}`} item={item} />
            ))}
        </ul>
    );
};

export default TaskCardHistoryTab;
